import { APPROVAL_RECORD_TYPES, type ApprovalMatrix, type ApprovalRule } from './approvals.js';
import { DomainError } from './errors.js';
import { parseSubject } from './forms/subject.js';
import {
  ACTIONS,
  type Action,
  evaluatePermission,
  MODULES,
  type Module,
  type PermissionCell,
  type PermissionMatrix,
  SCOPES,
  type Scope,
} from './permissions.js';
import { parseRoleKind, type RoleKind } from './role-kind.js';

/**
 * Matris editörünün saf kuralları (ADR 0042 §23, §24) [sorular #248, #249]: iki sürüm arasındaki
 * fark, kilitlenme koruması, Excel sayfalarının satır şeması ve rol kuralları. Matrisin kendi
 * doğrulaması `validatePermissionMatrix` ve `validateApprovalMatrix`'tedir; içe aktarma da ondan
 * geçer. Bu modül saftır.
 */

// ---------------------------------------------------------------------------------------------
// Yetki matrisi farkı

const MODULE_ORDER = new Map<string, number>(MODULES.map((module, index) => [module, index]));
const ACTION_ORDER = new Map<string, number>(ACTIONS.map((action, index) => [action, index]));

const cellKey = (cell: Pick<PermissionCell, 'roleId' | 'module' | 'action' | 'subject'>) =>
  `${cell.roleId}|${cell.module}|${cell.action}|${cell.subject ?? ''}`;

function compareCells(a: PermissionCell, b: PermissionCell): number {
  return (
    a.roleId.localeCompare(b.roleId) ||
    (MODULE_ORDER.get(a.module) ?? 0) - (MODULE_ORDER.get(b.module) ?? 0) ||
    (ACTION_ORDER.get(a.action) ?? 0) - (ACTION_ORDER.get(b.action) ?? 0) ||
    (a.subject ?? '').localeCompare(b.subject ?? '')
  );
}

/**
 * Hücrelerin karşılaştırılabilir biçimi: konusuz (modül) hücrede `none` hücresizlikle aynıdır ve
 * atılır; konulu hücrede `none` anlamlıdır (modül hücresini kapatır; ADR 0042 §8). Sıra rol, modül,
 * işlem ve konudur.
 */
export function normalizePermissionCells(cells: readonly PermissionCell[]): PermissionCell[] {
  return cells
    .filter((cell) => cell.scope !== 'none' || cell.subject !== undefined)
    .map((cell) => ({
      roleId: cell.roleId,
      module: cell.module,
      action: cell.action,
      scope: cell.scope,
      ...(cell.subject !== undefined ? { subject: cell.subject } : {}),
    }))
    .sort(compareCells);
}

export interface PermissionCellChange {
  roleId: string;
  module: Module;
  action: Action;
  subject?: string;
  from: Scope;
  to: Scope;
}

export interface JustificationChange {
  key: string;
  from: string | null;
  to: string | null;
}

export interface PermissionDiff {
  added: PermissionCell[];
  removed: PermissionCell[];
  changed: PermissionCellChange[];
  justifications: JustificationChange[];
}

/**
 * İki yetki matrisinin farkı (ADR 0042 §24): eklenen, kaldırılan ve kapsamı değişen hücreler
 * konularıyla ve değişen gerekçeler. Sürüm geçmişi, içe aktarma önizlemesi ve kaydetme penceresi
 * aynı farkı gösterir.
 */
export function diffPermissionMatrices(
  from: PermissionMatrix,
  to: PermissionMatrix,
): PermissionDiff {
  const before = new Map(normalizePermissionCells(from.cells).map((c) => [cellKey(c), c]));
  const after = new Map(normalizePermissionCells(to.cells).map((c) => [cellKey(c), c]));
  const added: PermissionCell[] = [];
  const removed: PermissionCell[] = [];
  const changed: PermissionCellChange[] = [];
  for (const [key, cell] of after) {
    const old = before.get(key);
    if (!old) added.push(cell);
    else if (old.scope !== cell.scope) {
      changed.push({
        roleId: cell.roleId,
        module: cell.module,
        action: cell.action,
        ...(cell.subject !== undefined ? { subject: cell.subject } : {}),
        from: old.scope,
        to: cell.scope,
      });
    }
  }
  for (const [key, cell] of before) if (!after.has(key)) removed.push(cell);
  const justifications: JustificationChange[] = [];
  const oldNotes = from.justifications ?? {};
  const newNotes = to.justifications ?? {};
  for (const key of [...new Set([...Object.keys(oldNotes), ...Object.keys(newNotes)])].sort()) {
    const a = oldNotes[key]?.trim() || null;
    const b = newNotes[key]?.trim() || null;
    if (a !== b) justifications.push({ key, from: a, to: b });
  }
  return {
    added: added.sort(compareCells),
    removed: removed.sort(compareCells),
    changed: changed.sort((a, b) => compareCells({ ...a, scope: a.to }, { ...b, scope: b.to })),
    justifications,
  };
}

// ---------------------------------------------------------------------------------------------
// Onay matrisi farkı

/**
 * Kuralın anahtarı: kayıt türü, giren roller (sırasız), konu ve tutar kademesi. Aynı türün ayrı
 * kuralları (kaptanın formu, kademeler) ayrı anahtardır; adım, süre ve gerekçe "değişen" sayılır.
 */
export function approvalRuleKey(rule: ApprovalRule): string {
  return JSON.stringify([
    rule.recordType,
    rule.enteredBy === 'any' ? '*' : [...rule.enteredBy].sort(),
    rule.subject ?? null,
    rule.minAmount ?? null,
    rule.maxAmount ?? null,
  ]);
}

/** Kuralın bütün alanlarının sırasız karşılaştırılabilir biçimi. */
function ruleSignature(rule: ApprovalRule): string {
  return JSON.stringify([
    approvalRuleKey(rule),
    rule.steps,
    rule.steps.map((_, i) => rule.officeSteps?.[i] === true),
    rule.deadlineHours ?? null,
    rule.selfApprovalReason ?? null,
  ]);
}

export interface ApprovalRuleChange {
  key: string;
  from: ApprovalRule;
  to: ApprovalRule;
}

export interface ApprovalDiff {
  added: ApprovalRule[];
  removed: ApprovalRule[];
  changed: ApprovalRuleChange[];
}

function groupRules(rules: readonly ApprovalRule[]): Map<string, ApprovalRule[]> {
  const groups = new Map<string, ApprovalRule[]>();
  for (const rule of rules) {
    const key = approvalRuleKey(rule);
    groups.set(key, [...(groups.get(key) ?? []), rule]);
  }
  return groups;
}

/** İki onay matrisinin kural anahtarına göre farkı (ADR 0042 §24). */
export function diffApprovalMatrices(from: ApprovalMatrix, to: ApprovalMatrix): ApprovalDiff {
  const before = groupRules(from.rules);
  const after = groupRules(to.rules);
  const added: ApprovalRule[] = [];
  const removed: ApprovalRule[] = [];
  const changed: ApprovalRuleChange[] = [];
  const keys = [...new Set([...before.keys(), ...after.keys()])].sort();
  for (const key of keys) {
    const a = before.get(key) ?? [];
    const b = after.get(key) ?? [];
    const signatures = (rules: readonly ApprovalRule[]) => rules.map(ruleSignature).sort();
    if (JSON.stringify(signatures(a)) === JSON.stringify(signatures(b))) continue;
    if (a.length === 1 && b.length === 1) {
      changed.push({ key, from: a[0] as ApprovalRule, to: b[0] as ApprovalRule });
      continue;
    }
    const kept = new Set(b.map(ruleSignature));
    const had = new Set(a.map(ruleSignature));
    removed.push(...a.filter((rule) => !kept.has(ruleSignature(rule))));
    added.push(...b.filter((rule) => !had.has(ruleSignature(rule))));
  }
  return { added, removed, changed };
}

// ---------------------------------------------------------------------------------------------
// Kilitlenme koruması

/**
 * Rollerin ofiste matrisi ve kullanıcıları yönetebilmesi: `settings.edit` filo hücresi (ADR 0042
 * §24). Gemi kapsamı ofiste (gemisiz kayıt) hiçbir şeyi kapsamaz.
 */
export function grantsMatrixAdmin(matrix: PermissionMatrix, roleIds: readonly string[]): boolean {
  return evaluatePermission(matrix, {
    actor: { userId: '', roleIds, vesselIds: [] },
    module: 'settings',
    action: 'edit',
    target: { vesselId: null },
  }).allowed;
}

/** Yönetici rolünü belirleyen yazma işlemleri (ADR 0150 §1). */
const ADMIN_WRITE_ACTIONS: ReadonlySet<string> = new Set(['create', 'edit', 'void']);

/**
 * Yönetici rolü (ADR 0150 §1; ADR 0042 Ek U39): `settings` modülünde yazma hücresi (create, edit,
 * void; kapsamı `none` dışında) veren rol. `settings` şirketin kurulumudur (matris, rol yönetimi,
 * şirket ayarı, gemi ve belge kataloğu); ofisteki yönetici tanımını (`grantsMatrixAdmin`,
 * `settings.edit` filo) kapsar ve gemi atamasında gemide geçerli ayar yazmasını da sayar. `users`
 * ve `devices` yazması ölçüt değildir: kaptanın gemide bilgisayar ekleme hücresi (`devices`, gemi)
 * rütbenin olağan işidir, gemide hesap yönetimi yoktur (ADR 0014 §6).
 */
export function isAdminRole(matrix: PermissionMatrix, roleId: string): boolean {
  return matrix.cells.some(
    (cell) =>
      cell.roleId === roleId &&
      cell.module === 'settings' &&
      ADMIN_WRITE_ACTIONS.has(cell.action) &&
      cell.scope !== 'none',
  );
}

/**
 * Yönetici yetkisi gemi atamalarıyla genişleyen rol (ADR 0150 §1): `settings` yazma hücresi gemi
 * ya da "kendi" kapsamlı. Ofiste bu kapsamlar kişinin bütün gemi atamalarındaki gemileri kapsar
 * (`actorRolesAt`, mimari belge §7.1); böyle bir şirket rolü taşıyan kişinin gemi atama listesi
 * yönetici yetkisinin sınırıdır. Filo kapsamı atamaya bağlı değildir.
 */
export function adminScopeFollowsVessels(matrix: PermissionMatrix, roleId: string): boolean {
  return matrix.cells.some(
    (cell) =>
      cell.roleId === roleId &&
      cell.module === 'settings' &&
      ADMIN_WRITE_ACTIONS.has(cell.action) &&
      (cell.scope === 'vessel' || cell.scope === 'own'),
  );
}

/** Matriste hücresi olan roller, sıralı ve tekil. */
const matrixRoleIds = (matrix: PermissionMatrix): string[] =>
  [...new Set(matrix.cells.map((cell) => cell.roleId))].sort();

/**
 * Matrisin yönetici rolleri (`isAdminRole`), sıralı ve tekil: ekranın düğme bayrakları yazma
 * yolunun tanımıyla aynı yerden gelir (ADR 0151 §3; tek tanım).
 */
export function adminRoleIdsOf(matrix: PermissionMatrix): string[] {
  return matrixRoleIds(matrix).filter((roleId) => isAdminRole(matrix, roleId));
}

/** Yönetici yetkisi gemi atamalarıyla genişleyen roller (`adminScopeFollowsVessels`), sıralı ve tekil. */
export function adminScopeRoleIdsOf(matrix: PermissionMatrix): string[] {
  return matrixRoleIds(matrix).filter((roleId) => adminScopeFollowsVessels(matrix, roleId));
}

export interface MatrixAdminCandidate {
  userId: string;
  active: boolean;
  /** Kişinin bugün geçerli şirket rolleri. */
  roleIds: readonly string[];
}

/**
 * Kilitlenme koruması (ADR 0042 §23, §24) [sorular #248, #249]: matrisin yeni sürümünde ya da
 * kullanıcı değişikliğinden sonra en az bir etkin kullanıcı `settings.edit` filo yetkisini korur;
 * yoksa şirket kendi kurulumunu bir daha değiştiremezdi (`MATRIX_LOCKOUT`).
 */
export function assertMatrixAdminRemains(
  matrix: PermissionMatrix,
  users: readonly MatrixAdminCandidate[],
): void {
  if (!users.some((user) => user.active && grantsMatrixAdmin(matrix, user.roleIds))) {
    throw new DomainError('MATRIX_LOCKOUT');
  }
}

/** Kişinin şirket yöneticisi olduğu günler (ADR 0150 §3): etkin kişinin yönetici rolü satırı. */
export interface AdminSpan {
  userId: string;
  /** İlk gün `YYYY-MM-DD`. */
  from: string;
  /** Son gün; `null` süresiz. */
  to: string | null;
}

const dayAfter = (day: string) =>
  new Date(Date.parse(`${day}T00:00:00.000Z`) + 86_400_000).toISOString().slice(0, 10);

/**
 * Kilitlenme koruması bütün gelecek için (ADR 0150 §3; ADR 0042 §23): değişiklikten önce en az
 * bir yöneticisi olan ama sonra hiç yöneticisi kalmayan, bugünden ileri ilk gün; yoksa `null`.
 * Kapsam yalnız satır başlangıçlarında ve bitişlerin ertesi gününde değişir; bu günlere bakmak
 * yeter. Önceden yöneticisiz olan gün (eski veri) ilgisiz bir değişikliği engellemez.
 */
export function firstLostAdminDay(
  before: readonly AdminSpan[],
  after: readonly AdminSpan[],
  today: string,
): string | null {
  const covered = (spans: readonly AdminSpan[], day: string) =>
    spans.some((span) => span.from <= day && (span.to === null || span.to >= day));
  const days = new Set([today]);
  for (const span of [...before, ...after]) {
    if (span.from > today) days.add(span.from);
    if (span.to !== null && span.to >= today) days.add(dayAfter(span.to));
  }
  for (const day of [...days].sort()) {
    if (covered(before, day) && !covered(after, day)) return day;
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// Excel sayfaları

/** Çalışma kitabının sayfaları (ADR 0042 §24): Permissions, Approvals, Justifications, Info. */
export const MATRIX_SHEETS = {
  permissions: 'Permissions',
  approvals: 'Approvals',
  justifications: 'Justifications',
  info: 'Info',
} as const;

export const PERMISSION_COLUMNS = ['Role', 'Module', 'Action', 'Subject', 'Scope'] as const;
export const APPROVAL_COLUMNS = [
  'Record type',
  'Subject',
  'Entered by',
  'Step 1',
  'Step 1 office',
  'Step 2',
  'Step 2 office',
  'Min amount',
  'Max amount',
  'Deadline (hours)',
  'Self-approval reason',
] as const;
export const JUSTIFICATION_COLUMNS = ['Role', 'Rule', 'Justification'] as const;

/** "Entered by" hücresinde herkes. */
export const ANY_ROLE = 'any';
/** Birden çok giren rolün ayırıcısı (rol adı `;` içeremez). */
const ROLE_SEPARATOR = ';';

/** Hücre değeri: metin, sayı, mantıksal ya da boş (exceljs okumasından). */
export type MatrixCellValue = string | number | boolean | null;

export interface MatrixRole {
  id: string;
  name: string;
}

export interface MatrixSheets {
  /** Her sayfanın ilk satırı başlıktır. */
  permissions: MatrixCellValue[][];
  approvals: MatrixCellValue[][];
  justifications: MatrixCellValue[][];
}

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

/**
 * Matrisi Excel sayfalarına çevirir (ADR 0042 §24): rol adları, modül, işlem, kapsam ve kayıt türü
 * kodlarıyla; modül hücresinde `none` yazılmaz. Gerekçe anahtarı `<modül>.<işlem>:<rol>` iki
 * sütuna ayrılır.
 */
export function matrixToSheets(input: {
  permission: PermissionMatrix;
  approval: ApprovalMatrix;
  roles: readonly MatrixRole[];
}): MatrixSheets {
  const names = new Map(input.roles.map((role) => [role.id, role.name]));
  const name = (id: string) => names.get(id) ?? id;
  const permissions: MatrixCellValue[][] = [
    [...PERMISSION_COLUMNS],
    ...normalizePermissionCells(input.permission.cells).map((cell) => [
      name(cell.roleId),
      cell.module,
      cell.action,
      cell.subject ?? null,
      cell.scope,
    ]),
  ];
  const approvals: MatrixCellValue[][] = [
    [...APPROVAL_COLUMNS],
    ...input.approval.rules.map((rule) => [
      rule.recordType,
      rule.subject ?? null,
      rule.enteredBy === 'any' ? ANY_ROLE : rule.enteredBy.map(name).join(`${ROLE_SEPARATOR} `),
      rule.steps[0] ? name(rule.steps[0]) : null,
      yesNo(rule.officeSteps?.[0] === true),
      rule.steps[1] ? name(rule.steps[1]) : null,
      yesNo(rule.officeSteps?.[1] === true),
      rule.minAmount ?? null,
      rule.maxAmount ?? null,
      rule.deadlineHours ?? null,
      rule.selfApprovalReason ?? null,
    ]),
  ];
  const justifications: MatrixCellValue[][] = [
    [...JUSTIFICATION_COLUMNS],
    ...Object.entries(input.permission.justifications ?? {})
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, text]) => {
        const at = key.lastIndexOf(':');
        return [name(key.slice(at + 1)), key.slice(0, at), text];
      }),
  ];
  return { permissions, approvals, justifications };
}

export type MatrixSheetName = 'permissions' | 'approvals' | 'justifications';

export interface MatrixRowError {
  sheet: MatrixSheetName;
  /** Excel satır numarası (başlık 1). */
  row: number;
  field: string;
  code:
    | 'REQUIRED'
    | 'INVALID_VALUE'
    | 'UNKNOWN_ROLE'
    | 'UNKNOWN_MODULE'
    | 'UNKNOWN_ACTION'
    | 'UNKNOWN_SCOPE'
    | 'UNKNOWN_RECORD_TYPE'
    | 'INVALID_SUBJECT'
    | 'DUPLICATE_ROW';
}

export interface ParsedMatrixSheets {
  cells: PermissionCell[];
  rules: ApprovalRule[];
  justifications: Record<string, string>;
  /** Satır hataları; boş değilse matris yazılmaz. */
  errors: MatrixRowError[];
}

const text = (value: MatrixCellValue | undefined): string =>
  value === null || value === undefined ? '' : String(value).trim();

const empty = (row: readonly MatrixCellValue[]) => row.every((value) => text(value) === '');

function assertHeader(rows: readonly MatrixCellValue[][], columns: readonly string[]) {
  const header = rows[0] ?? [];
  if (columns.some((column, index) => text(header[index]) !== column)) {
    throw new DomainError('INVALID_HEADER', header.map(text).join('|'));
  }
}

function parseBoolean(value: MatrixCellValue | undefined): boolean | undefined {
  if (value === null || value === undefined || text(value) === '') return false;
  if (typeof value === 'boolean') return value;
  const word = text(value).toLowerCase();
  if (['yes', 'true', 'y', '1'].includes(word)) return true;
  if (['no', 'false', 'n', '0'].includes(word)) return false;
  return undefined;
}

function parseNumber(value: MatrixCellValue | undefined): number | null | undefined {
  if (value === null || value === undefined || text(value) === '') return null;
  const number = typeof value === 'number' ? value : Number(text(value));
  return Number.isFinite(number) ? number : undefined;
}

/**
 * Excel sayfalarını matrise çevirir (ADR 0042 §24) [sorular #249]. Bilinmeyen rol, modül, işlem,
 * kapsam, kayıt türü ve konu satır numarasıyla bildirilir; başlığı uymayan sayfa
 * `INVALID_HEADER`. Boş satırlar atlanır, numaralar korunur. Matris kuralları (verilemeyen hücre,
 * gerekçe, kademe) ayrıca `validatePermissionMatrix` ve `validateApprovalMatrix`'ten geçer.
 */
export function parseMatrixSheets(
  sheets: MatrixSheets,
  roles: readonly MatrixRole[],
): ParsedMatrixSheets {
  assertHeader(sheets.permissions, PERMISSION_COLUMNS);
  assertHeader(sheets.approvals, APPROVAL_COLUMNS);
  if (sheets.justifications.length > 0) assertHeader(sheets.justifications, JUSTIFICATION_COLUMNS);
  const byName = new Map(roles.map((role) => [role.name.trim().toLocaleLowerCase('en'), role.id]));
  const roleId = (value: string) => byName.get(value.trim().toLocaleLowerCase('en'));
  const errors: MatrixRowError[] = [];

  const cells: PermissionCell[] = [];
  const seenCells = new Set<string>();
  sheets.permissions.slice(1).forEach((values, index) => {
    const row = index + 2;
    if (empty(values)) return;
    const fail = (field: string, code: MatrixRowError['code']) =>
      errors.push({ sheet: 'permissions', row, field, code });
    const before = errors.length;
    const [roleValue, moduleValue, actionValue, subjectValue, scopeValue] = values.map(text);
    const id = roleValue ? roleId(roleValue) : undefined;
    if (!roleValue) fail('role', 'REQUIRED');
    else if (!id) fail('role', 'UNKNOWN_ROLE');
    if (!(MODULES as readonly string[]).includes(moduleValue ?? ''))
      fail('module', 'UNKNOWN_MODULE');
    if (!(ACTIONS as readonly string[]).includes(actionValue ?? ''))
      fail('action', 'UNKNOWN_ACTION');
    if (subjectValue) {
      try {
        parseSubject(subjectValue);
        if (moduleValue !== 'ism_forms') fail('subject', 'INVALID_SUBJECT');
      } catch {
        fail('subject', 'INVALID_SUBJECT');
      }
    }
    if (!(SCOPES as readonly string[]).includes(scopeValue ?? '')) fail('scope', 'UNKNOWN_SCOPE');
    if (errors.length > before || !id) return;
    const cell: PermissionCell = {
      roleId: id,
      module: moduleValue as Module,
      action: actionValue as Action,
      scope: scopeValue as Scope,
      ...(subjectValue ? { subject: subjectValue } : {}),
    };
    const key = cellKey(cell);
    if (seenCells.has(key)) {
      fail('role', 'DUPLICATE_ROW');
      return;
    }
    seenCells.add(key);
    cells.push(cell);
  });

  const rules: ApprovalRule[] = [];
  sheets.approvals.slice(1).forEach((values, index) => {
    const row = index + 2;
    if (empty(values)) return;
    const fail = (field: string, code: MatrixRowError['code']) =>
      errors.push({ sheet: 'approvals', row, field, code });
    const before = errors.length;
    const recordType = text(values[0]);
    const subject = text(values[1]);
    const enteredByValue = text(values[2]);
    const step1Value = text(values[3]);
    const step1Office = parseBoolean(values[4]);
    const step2Value = text(values[5]);
    const step2Office = parseBoolean(values[6]);
    const minAmount = parseNumber(values[7]);
    const maxAmount = parseNumber(values[8]);
    const deadline = parseNumber(values[9]);
    const selfApprovalReason = text(values[10]);

    if (!recordType) fail('recordType', 'REQUIRED');
    else if (!(APPROVAL_RECORD_TYPES as readonly string[]).includes(recordType)) {
      fail('recordType', 'UNKNOWN_RECORD_TYPE');
    }
    if (subject) {
      try {
        parseSubject(subject);
        if (recordType !== 'ism_form') fail('subject', 'INVALID_SUBJECT');
      } catch {
        fail('subject', 'INVALID_SUBJECT');
      }
    }
    let enteredBy: string[] | 'any' = 'any';
    if (!enteredByValue) fail('enteredBy', 'REQUIRED');
    else if (enteredByValue.toLowerCase() !== ANY_ROLE) {
      const names = enteredByValue
        .split(ROLE_SEPARATOR)
        .map((part) => part.trim())
        .filter(Boolean);
      const ids = names.map(roleId);
      if (names.length === 0 || ids.some((id) => id === undefined)) {
        fail('enteredBy', 'UNKNOWN_ROLE');
      } else {
        enteredBy = ids as string[];
      }
    }
    const step1 = step1Value ? roleId(step1Value) : undefined;
    const step2 = step2Value ? roleId(step2Value) : undefined;
    if (step1Value && !step1) fail('step1', 'UNKNOWN_ROLE');
    if (!step1Value && step2Value) fail('step1', 'REQUIRED');
    if (step1Office === undefined || (step1Office && !step1Value)) {
      fail('step1Office', 'INVALID_VALUE');
    }
    if (step2Value && !step2) fail('step2', 'UNKNOWN_ROLE');
    if (step2Office === undefined || (step2Office && !step2Value)) {
      fail('step2Office', 'INVALID_VALUE');
    }
    if (minAmount === undefined || (minAmount !== null && minAmount < 0)) {
      fail('minAmount', 'INVALID_VALUE');
    }
    if (maxAmount === undefined || (maxAmount !== null && maxAmount <= 0)) {
      fail('maxAmount', 'INVALID_VALUE');
    }
    if (
      deadline === undefined ||
      (deadline !== null && (!Number.isInteger(deadline) || deadline <= 0))
    ) {
      fail('deadlineHours', 'INVALID_VALUE');
    }
    if (errors.length > before) return;
    const steps = [step1, step2].filter((id): id is string => id !== undefined);
    const rule: ApprovalRule = { recordType, enteredBy, steps };
    const office = [step1Office === true, step2Office === true].slice(0, steps.length);
    if (office.some(Boolean)) rule.officeSteps = office;
    if (deadline !== null && deadline !== undefined) rule.deadlineHours = deadline;
    if (selfApprovalReason) rule.selfApprovalReason = selfApprovalReason;
    if (minAmount !== null && minAmount !== undefined) rule.minAmount = minAmount;
    if (maxAmount !== null && maxAmount !== undefined) rule.maxAmount = maxAmount;
    if (subject) rule.subject = subject;
    rules.push(rule);
  });

  const justifications: Record<string, string> = {};
  sheets.justifications.slice(1).forEach((values, index) => {
    const row = index + 2;
    if (empty(values)) return;
    const fail = (field: string, code: MatrixRowError['code']) =>
      errors.push({ sheet: 'justifications', row, field, code });
    const before = errors.length;
    const roleValue = text(values[0]);
    const rule = text(values[1]);
    const note = text(values[2]);
    const id = roleValue ? roleId(roleValue) : undefined;
    if (!roleValue) fail('role', 'REQUIRED');
    else if (!id) fail('role', 'UNKNOWN_ROLE');
    const [module, action, extra] = rule.split('.');
    if (
      extra !== undefined ||
      !(MODULES as readonly string[]).includes(module ?? '') ||
      !(ACTIONS as readonly string[]).includes(action ?? '')
    ) {
      fail('rule', 'INVALID_VALUE');
    }
    if (!note) fail('justification', 'REQUIRED');
    if (errors.length > before || !id) return;
    justifications[`${rule}:${id}`] = note;
  });

  return { cells, rules, justifications, errors };
}

// ---------------------------------------------------------------------------------------------
// Rol kuralları

export const MAX_ROLE_NAME_LENGTH = 80;
export const MAX_ROLE_DESCRIPTION_LENGTH = 500;

export interface RoleInput {
  name: string;
  description: string | null;
  pmsResponsible: boolean;
  /** Rol türü (ADR 0143); verilmezse yeni rol `any`, var olan rol türünü korur. */
  kind?: RoleKind;
}

/**
 * Rol girdisi (ADR 0042 §23): ad kırpılır, 1–80 karakter; `;` Excel'de giren rollerin ayırıcısıdır
 * ve ad içinde olamaz (`ROLE_NAME_INVALID`). Boş açıklama `null`.
 */
export function parseRoleInput(input: RoleInput): RoleInput {
  const name = input.name.trim();
  if (name === '') throw new DomainError('ROLE_NAME_REQUIRED');
  if (name.length > MAX_ROLE_NAME_LENGTH) throw new DomainError('ROLE_NAME_TOO_LONG');
  if (name.includes(ROLE_SEPARATOR) || /\p{Cc}/u.test(name)) {
    throw new DomainError('ROLE_NAME_INVALID');
  }
  const description = input.description?.trim() || null;
  if (description !== null && description.length > MAX_ROLE_DESCRIPTION_LENGTH) {
    throw new DomainError('ROLE_DESCRIPTION_TOO_LONG');
  }
  return {
    name,
    description,
    pmsResponsible: input.pmsResponsible,
    ...(input.kind === undefined ? {} : { kind: parseRoleKind(input.kind) }),
  };
}

/**
 * Varsayılan sekiz rolün adı değiştirilemez (ADR 0042 §23) [sorular #248]: provisioning ve
 * `ensureDefaultMatrices` varsayılan rolleri adla bulur. Türü de değişmez (ADR 0150 §2): tür
 * rolün nerede verileceğini belirler (ADR 0143); varsayılan rollerin türü sistemin kuruluşudur ve
 * Company Administrator'ın türü yönetici rolünün nereden verileceğini açar. Açıklama ve PMS
 * sorumlusu işareti serbesttir; tür verilmeyen ya da aynı kalan düzenleme geçer. Kilitten önce
 * türü değiştirilmiş varsayılan rol yalnız sistem türüne (`systemKind`) döner: yanlış tür
 * kalıcı olmaz, başka türe geçiş yine kilitlidir.
 */
export function assertRoleChange(
  current: { name: string; isDefault: boolean; kind?: RoleKind; systemKind?: RoleKind | null },
  next: RoleInput,
): void {
  if (!current.isDefault) return;
  const kindChanged =
    next.kind !== undefined && next.kind !== current.kind && next.kind !== current.systemKind;
  if (next.name !== current.name || kindChanged) {
    throw new DomainError('DEFAULT_ROLE_LOCKED', current.name);
  }
}
